<template>
  <div
    class="w-full h-[180px] bg-[#f8fff8] rounded-[14px] p-[16px] flex flex-col justify-between relative shadow-none hover:shadow-none hover:border-[#01d680] transition-colors duration-200 group cursor-pointer"
    :class="isSelected ? 'border-[#01d680] ring-2 ring-[#01d680]' : 'border border-[#22ec9b]'"
    @click="$emit('instantiate', template)"
  >
    <!-- Selection Checkbox -->
    <div
      class="absolute top-[12px] left-[12px] z-30 transition-opacity"
      :class="isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'"
      @click.stop.prevent="$emit('toggle-select', template.id)"
    >
      <input
        type="checkbox"
        :checked="isSelected"
        class="w-[16px] h-[16px] accent-[#01d680] cursor-pointer rounded"
        @click.stop
        @change="$emit('toggle-select', template.id)"
      >
    </div>

    <!-- Top row: Title + 3-dot Kebab Dropdown -->
    <div
      class="flex justify-between items-start w-full gap-2 transition-all"
      :class="{ 'pl-[22px]': isSelected }"
    >
      <h3
        class="text-[14px] font-bold text-black font-['Raleway',sans-serif] line-clamp-2 min-w-0 flex-1 m-0 leading-tight"
        :title="template.name"
      >
        {{ template.name }}
      </h3>

      <div
        class="w-[20px] h-[20px] flex flex-col justify-center items-center cursor-pointer shrink-0 relative group/kebab gap-[2px] ml-[6px]"
        @click.stop.prevent="toggleKebab"
      >
        <div class="w-[3px] h-[3px] bg-black rounded-full" />
        <div class="w-[3px] h-[3px] bg-black rounded-full" />
        <div class="w-[3px] h-[3px] bg-black rounded-full" />

        <!-- Stateful Kebab Dropdown -->
        <div
          v-if="isOpen"
          class="absolute top-[24px] right-0 bg-white border border-black/10 rounded-[14px] shadow-2xl z-[100] flex flex-col w-[170px] py-2"
          @click.stop
        >
          <a
            href="#"
            class="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 no-underline text-left flex items-center gap-2"
            @click.prevent="$emit('instantiate', template); isOpen = false"
          >
            <svg
              class="w-3.5 h-3.5 text-[#01d680]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
            Instantiate Cohort
          </a>
          <a
            href="#"
            class="px-4 py-2 text-xs text-gray-700 hover:bg-gray-100 no-underline text-left flex items-center gap-2"
            @click.prevent="$emit('clone', template); isOpen = false"
          >
            <svg
              class="w-3.5 h-3.5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            Clone Template
          </a>
          <a
            href="#"
            class="px-4 py-2 text-xs text-gray-700 hover:bg-gray-100 no-underline text-left flex items-center gap-2"
            @click.prevent="$emit('archive', template); isOpen = false"
          >
            <svg
              class="w-3.5 h-3.5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
              />
            </svg>
            Archive Template
          </a>
          <a
            href="#"
            class="px-4 py-2 text-xs text-red-600 hover:bg-red-50 no-underline text-left flex items-center gap-2"
            @click.prevent="$emit('delete', template); isOpen = false"
          >
            <svg
              class="w-3.5 h-3.5 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Delete Template
          </a>
        </div>
      </div>
    </div>

    <!-- Metadata: SETA and Programme Count (STRICT INVARIANT: ZERO STATUS PILLS) -->
    <div class="flex flex-col gap-[6px] mt-[12px] w-full flex-grow">
      <div class="flex items-center gap-[6px] text-[12px] font-['Raleway',sans-serif] text-[#5b5c60]">
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="shrink-0"
        >
          <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
          <path d="M6 12H4a2 2 0 0 0-2 2v8h4" />
          <path d="M18 9h2a2 2 0 0 1 2 2v11h-4" />
          <path d="M10 6h4" />
          <path d="M10 10h4" />
          <path d="M10 14h4" />
          <path d="M10 18h4" />
        </svg>
        <span
          class="truncate"
          :title="template.seta_type"
        >
          {{ template.seta_type }}
        </span>
      </div>

      <div class="flex items-center gap-[6px] text-[12px] font-['Raleway',sans-serif] text-[#5b5c60]">
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          class="shrink-0"
        >
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
        <span class="truncate">
          {{ template.programmes_count }} {{ template.programmes_count === 1 ? 'Programme' : 'Programmes' }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CohortTemplateCard',
  props: {
    template: {
      type: Object,
      required: true
    },
    isSelected: {
      type: Boolean,
      default: false
    }
  },
  emits: ['instantiate', 'clone', 'archive', 'delete', 'toggle-select'],
  data () {
    return {
      isOpen: false
    }
  },
  mounted () {
    this.handleOutsideClick = () => {
      this.isOpen = false
    }
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeUnmount () {
    if (this.handleOutsideClick) {
      document.removeEventListener('click', this.handleOutsideClick)
    }
  },
  methods: {
    toggleKebab () {
      this.isOpen = !this.isOpen
    },
    closeKebab () {
      this.isOpen = false
    }
  }
}
</script>
